export interface SettingsFormState {
  language: string;
  theme: string;
  currency: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    profileVisibility: string;
    dataSharing: boolean;
  };
  password: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
}

export class SettingsViewModel {
  private static instance: SettingsViewModel;

  private constructor() {}

  static getInstance(): SettingsViewModel {
    if (!SettingsViewModel.instance) {
      SettingsViewModel.instance = new SettingsViewModel();
    }
    return SettingsViewModel.instance;
  }

  getInitialState(): SettingsFormState {
    // Récupérer tous les paramètres depuis sessionStorage
    const savedLanguage = sessionStorage.getItem('language') || 'fr';
    const savedTheme = sessionStorage.getItem('theme') || 'light';
    const savedCurrency = sessionStorage.getItem('currency') || 'EUR';
    const savedNotifications = sessionStorage.getItem('notifications');
    const savedPrivacy = sessionStorage.getItem('privacy');
    
    return {
      language: savedLanguage,
      theme: savedTheme,
      currency: savedCurrency,
      notifications: savedNotifications ? JSON.parse(savedNotifications) : {
        email: true,
        push: true,
        sms: false,
      },
      privacy: savedPrivacy ? JSON.parse(savedPrivacy) : {
        profileVisibility: 'public',
        dataSharing: true,
      },
      password: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    };
  }

  async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔍 Debug changePassword:', { userId, currentPassword: '***', newPassword: '***', token: token ? 'present' : 'missing' });
      
      if (!token) {
        return { success: false, message: 'Token d\'authentification manquant' };
      }

      const url = `${process.env.REACT_APP_API_URL}api/users/${userId}/change-password`;
      const body = JSON.stringify({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      
      console.log('🌐 API Call:', { url, method: 'POST', body });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: body,
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json();
      console.log('📡 Response body:', result);

      if (response.ok) {
        return { success: true, message: result.message || 'Mot de passe mis à jour avec succès' };
      } else {
        console.error('❌ API Error:', { status: response.status, result });
        return { success: false, message: result.message || `Erreur ${response.status}: ${response.statusText}` };
      }
    } catch (error) {
      console.error('❌ Erreur lors du changement de mot de passe:', error);
      return { success: false, message: 'Erreur de connexion au serveur' };
    }
  }

  validatePasswordForm(passwordData: SettingsFormState['password']): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!passwordData.currentPassword) {
      errors.push('Le mot de passe actuel est requis');
    }

    if (!passwordData.newPassword) {
      errors.push('Le nouveau mot de passe est requis');
    } else if (passwordData.newPassword.length < 6) {
      errors.push('Le nouveau mot de passe doit contenir au moins 6 caractères');
    }

    if (!passwordData.confirmPassword) {
      errors.push('La confirmation du mot de passe est requise');
    }

    if (passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword) {
      errors.push('Les mots de passe ne correspondent pas');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  async updateSettings(userId: string, settingsData: Partial<SettingsFormState>): Promise<{ success: boolean; message: string }> {
    try {
      // Sauvegarder la devise en sessionStorage
      if (settingsData.currency) {
        sessionStorage.setItem('currency', settingsData.currency);
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        return { success: false, message: 'Token d\'authentification manquant' };
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/users/${userId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settingsData),
      });

      const result = await response.json();

      if (response.ok) {
        return { success: true, message: 'Paramètres mis à jour avec succès' };
      } else {
        return { success: false, message: result.message || 'Erreur lors de la mise à jour des paramètres' };
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      return { success: false, message: 'Erreur de connexion au serveur' };
    }
  }
}

export const useSettingsViewModel = () => {
  const viewModel = SettingsViewModel.getInstance();
  
  const changePassword = async (userId: string, currentPassword: string, newPassword: string) => {
    return await viewModel.changePassword(userId, currentPassword, newPassword);
  };

  const validatePasswordForm = (passwordData: SettingsFormState['password']) => {
    return viewModel.validatePasswordForm(passwordData);
  };

  const updateSettings = async (userId: string, settingsData: Partial<SettingsFormState>) => {
    return await viewModel.updateSettings(userId, settingsData);
  };

  return {
    changePassword,
    validatePasswordForm,
    updateSettings,
    getInitialState: viewModel.getInitialState.bind(viewModel),
  };
}; 